<template>
  <CustomSuspense
    :is-loading="isLoading"
    :is-empty="isEmpty(rows)"
  >
    <template #loading>
      <LoadingTable
        :row-cnt="rowCount"
        :cols="cols"
      />
    </template>
    <template #empty>
      <NoDataPreview />
    </template>
    <template #default>
      <TransactionTable
        class="transaction-infinite-table"
        :columns="cols"
        :rows="vmRows"
        sticky-header
      >
        <template #item-transactionHash="{row}">
          <TnxLink
            class="link"
            :hash="row.original.transactionHash"
          />
        </template>
        <template #item-blockNumber="{row}">
          <BcLink
            :content="row.original.blockNumber"
            :to="routes.getBlockDetailPageWithTab(row.original.blockNumber, blockDetailPageType.block)"
          />
        </template>
        <template #item-method="{row}">
          <Chip
            v-if="row.original.method"
            v-tooltip="row.original.method"
            :value="row.original.method"
            class="max-w-[200px] truncate"
          />
        </template>
        <template #item-fromAddressInfo="{row}">
          <AddressCopyLink
            class="link"
            :tooltip="row.original.fromAddressInfo.address"
            :address="row.original.fromAddressInfo.address"
            :name="row.original.fromAddressInfo.name"
            :type="row.original.fromAddressInfo.type"
            hide-eoa-icon
          />
        </template>
        <template #item-rightArrow>
          <FontAwesomeIcon icon="arrow-right" />
        </template>
        <template #item-toAddressInfo="{row}">
          <AddressCopyLink
            class="link"
            :tooltip="row.original.toAddressInfo.address"
            :address="row.original.toAddressInfo.address"
            :name="row.original.toAddressInfo.name"
            :type="row.original.toAddressInfo.type"
            hide-eoa-icon
          />
        </template>
      </TransactionTable>
    </template>
  </CustomSuspense>
</template>

<script setup lang="ts">
import { ColumnDef } from '@tanstack/vue-table'
import { useVModel } from '@vueuse/core'

import AddressCopyLink from '~/components/link/AddressCopyLink.vue'
import TnxLink from '~/components/link/TnxLink.vue'
import NoDataPreview from '~/components/preview/NoDataPreview.vue'
import CustomSuspense from '~/components/suspense/CustomSuspense.vue'
import { TRANSACTION_TABLE_COLUMN } from '~/constants/transaction.constants'
import { TransactionModel } from '~/types/model/transaction.model'
import BcLink from '~/ui/Link/BcLink.vue'
import CommonTable from '~/ui/Table/tanstack/CommonTable.vue'

interface IProps {
  cols?: Array<ColumnDef<TransactionModel>>
  rows: Array<TransactionModel>
  isLoading: boolean
  rowCount: number
}

const props = withDefaults(defineProps<IProps>(), {
  cols: () => TRANSACTION_TABLE_COLUMN,
})

const vmRows = useVModel(props, 'rows')

const TransactionTable = CommonTable<TransactionModel>
</script>

<style scoped lang="scss">
.transaction-infinite-table {
  :deep(table) {
    @apply rounded-lg bg-primary/10 border-primary/10 border-x border-b #{!important};
  }

  :deep(thead), :deep(tr:has(th)) {
    @apply rounded-lg #{!important};
  }

  :deep(th:first-child) {
    @apply rounded-tl-lg #{!important};
  }

  :deep(th:last-child) {
    @apply rounded-tr-lg #{!important};
  }
}

.link {
  max-width: 200px;

  @include over_xl {
    max-width: 270px;
  }
}
</style>